import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Gift, Mail, FileText, Shield, Sparkles, Info, Users, ChevronDown } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import SEOHead from "@/components/SEOHead";
import { Helmet } from "react-helmet-async";
import appScreenshot1 from "@/assets/app-screenshot-1.png";
import appScreenshot2 from "@/assets/app-screenshot-2.png";
import appScreenshot3 from "@/assets/app-screenshot-3.png";
import appScreenshot4 from "@/assets/app-screenshot-4.png";
import heroLifestyle from "@/assets/hero-lifestyle.webp";

const Index = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);

  // Detect mobile to disable heavy parallax (avoids jank on iOS/Android)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Check if native early
  const isNative = 
    navigator.userAgent.toLowerCase().includes('despia') || 
    Capacitor.isNativePlatform();

  // Track mount state
  useEffect(() => {
    setMounted(true);
  }, []);

  // Auto-redirect native apps to boot screen (never blank)
  useEffect(() => {
    if (isNative) {
      navigate('/boot', { replace: true });
    }
  }, [navigate, isNative]);

  // Fallback timer: if still on Index after 2s in native, show recovery UI
  useEffect(() => {
    if (!isNative) return;
    const timer = window.setTimeout(() => setShowFallback(true), 2000);
    return () => window.clearTimeout(timer);
  }, [isNative]);

  // For native apps, show a loading state while redirecting to prevent blank screen
  if (isNative) {
    return (
      <div 
        className="min-h-screen bg-gradient-secondary flex items-center justify-center"
        style={{
          paddingTop: 'var(--safe-area-top, 0)',
          paddingBottom: 'var(--safe-area-bottom, 0)',
        }}
      >
        <div className="text-center space-y-4">
          <img src="/logo.webp" alt="GistGift" className="w-20 h-20 mx-auto" onError={(e) => { (e.target as HTMLImageElement).src = '/logo.png'; }} />
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          {mounted && showFallback && (
            <div className="mt-6 space-y-3">
              <p className="text-sm text-muted-foreground">Taking longer than expected...</p>
              <Button 
                onClick={() => navigate('/boot', { replace: true })} 
                className="min-h-[44px]"
              >
                Continue to App
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="GistGift: AI Gift Finder & Personalized Recommendations"
        description="Find the perfect gift with AI. Upload a photo and GistGift analyzes preferences to suggest personalized, thoughtful recommendations for any occasion."
        canonical="/"
      />
      <Helmet>
        <link rel="preload" as="image" href={heroLifestyle} fetchPriority="high" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "name": "GistGift",
              "url": "https://gistgift.shop",
              "logo": "https://gistgift.shop/logo.webp",
              "sameAs": [
                "https://www.tiktok.com/@gistgift2",
                "https://x.com/gistgiftapp"
              ]
            },
            {
              "@type": "WebSite",
              "name": "GistGift",
              "url": "https://gistgift.shop"
            },
            {
              "@type": "SoftwareApplication",
              "name": "GistGift",
              "operatingSystem": "iOS, Android",
              "applicationCategory": "LifestyleApplication",
              "description": "AI-powered gift finder that suggests personalized gift ideas from a photo.",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
              "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "ratingCount": "120" }
            }
          ]
        })}</script>
      </Helmet>
      <main
        className="min-h-[100svh] bg-background overflow-x-hidden"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Hero Section with lifestyle background */}
        <section
          className="relative w-full"
          style={{
            backgroundImage: `url(${heroLifestyle})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: isMobile ? 'scroll' : 'fixed',
          }}
        >
          {/* Semi-transparent white overlay for readability */}
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] pointer-events-none" aria-hidden="true" />
          <div className="relative container mx-auto px-4 pt-4 sm:pt-8 pb-8 sm:pb-12 max-w-6xl">
            <div className="text-center">
              <img src="/logo-96.webp" alt="GistGift Logo" className="mx-auto h-16 w-16 sm:h-24 sm:w-24 mb-3 sm:mb-6" width="96" height="96" fetchPriority="high" onError={(e) => { (e.target as HTMLImageElement).src = '/logo-optimized.webp'; }} />
          <h1 className="text-[42px] leading-tight sm:text-5xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            GistGift
          </h1>
          {/* App Store Banner */}
          <div className="mt-6 sm:mt-8">
            {/* App Screenshots */}
            <div className="mb-6 sm:mb-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto">
              {[
                { src: appScreenshot1, alt: "GistGift AI Gift Finder screen" },
                { src: appScreenshot2, alt: "GistGift smart gift giving" },
                { src: appScreenshot3, alt: "GistGift curated gift ideas" },
                { src: appScreenshot4, alt: "GistGift AI-powered gift detail" },
              ].map((shot) => (
                <img
                  key={shot.alt}
                  src={shot.src}
                  alt={shot.alt}
                  loading="lazy"
                  decoding="async"
                  width="390"
                  height="844"
                  className="w-full h-auto aspect-[390/844] object-contain rounded-2xl shadow-lg bg-muted/30"
                />
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href="https://apps.apple.com/us/app/gistgift/id6755075334"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="h-14 w-auto"
                  height="56"
                  fetchPriority="high"
                  loading="eager"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.despia.gistgift"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-[60px] w-auto"
                  height="60"
                  loading="eager"
                />
              </a>
            </div>
          </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 mt-8 sm:mt-12">
          <Card>
            <CardHeader>
              <Gift className="h-8 w-8 mb-2 text-primary" />
              <CardTitle>Smart Recommendations</CardTitle>
              <CardDescription>
                AI analyzes photos and preferences to suggest perfect gifts
              </CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <Sparkles className="h-8 w-8 mb-2 text-primary" />
              <CardTitle>Personalized Results</CardTitle>
              <CardDescription>
                Get 10 tailored gift ideas based on occasion and budget
              </CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <Shield className="h-8 w-8 mb-2 text-primary" />
              <CardTitle>Privacy First</CardTitle>
              <CardDescription>
                Your data is private and secure, never shared with third parties
              </CardDescription>
            </CardHeader>
          </Card>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-6 sm:py-8 max-w-6xl">

        {/* Subscription Info */}
        <Card className="mb-8 sm:mb-12">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle>Subscription Plans</CardTitle>
            <CardDescription>Choose the plan that works for you</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
              <div className="border rounded-lg p-4 sm:p-6">
                <h3 className="text-xl font-semibold mb-2">Free</h3>
                <p className="text-3xl font-bold mb-3">$0</p>
                <ul className="space-y-2 text-sm">
                  <li>✓ 1 photo per scan</li>
                  <li>✓ 3 scans per month</li>
                  <li>✓ Save up to 5 gifts</li>
                  <li>✓ Basic gift recommendations</li>
                </ul>
              </div>
              <div className="border rounded-lg p-4 sm:p-6 border-primary">
                <h3 className="text-xl font-semibold mb-2">Premium - Monthly</h3>
                <p className="text-3xl font-bold mb-3">$5.99<span className="text-base font-normal">/month</span></p>
                <ul className="space-y-2 text-sm">
                  <li>✓ 10 photos per scan</li>
                  <li>✓ Unlimited scans</li>
                  <li>✓ Unlimited saved gifts</li>
                  <li>✓ Ad-free experience</li>
                  <li>✓ Extended notes (1000 chars)</li>
                </ul>
              </div>
              <div className="border rounded-lg p-4 sm:p-6 border-primary relative mt-3 md:mt-0">
                <div className="absolute -top-3 right-3 sm:right-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full shadow">
                  Save 20%
                </div>
                <h3 className="text-xl font-semibold mb-2">Premium - Yearly</h3>
                <p className="text-3xl font-bold mb-1">$59.99<span className="text-base font-normal">/year</span></p>
                <p className="text-sm text-muted-foreground mb-3">Just $5.00/month</p>
                <ul className="space-y-2 text-sm">
                  <li>✓ 10 photos per scan</li>
                  <li>✓ Unlimited scans</li>
                  <li>✓ Unlimited saved gifts</li>
                  <li>✓ Ad-free experience</li>
                  <li>✓ Extended notes (1000 chars)</li>
                </ul>
              </div>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="mt-4 sm:mt-6 text-xs text-muted-foreground hover:text-foreground">
                  <Info className="h-3 w-3 mr-1" />
                  Subscription Terms
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Auto-Renewable Subscription Terms</DialogTitle>
                  <DialogDescription>
                    Please review the following subscription terms
                  </DialogDescription>
                </DialogHeader>
                <ul className="text-sm space-y-2 mt-4">
                  <li>• Payment will be charged to your iTunes or Google Play account at confirmation of purchase</li>
                  <li>• Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current period</li>
                  <li>• Your account will be charged for renewal within 24 hours prior to the end of the current period</li>
                  <li>• You can manage your subscription and turn off auto-renewal in your account settings after purchase</li>
                  <li>• Any unused portion of a free trial period will be forfeited when you purchase a subscription</li>
                </ul>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>


        {/* Follow GistGift Section */}
        <Card className="mb-8 sm:mb-12 text-center">
          <CardHeader>
            <Users className="h-10 w-10 mx-auto mb-2 text-primary" />
            <CardTitle>Follow GistGift</CardTitle>
            <CardDescription>
              Stay updated with the latest features and gift ideas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button variant="outline" asChild className="gap-2">
                <a 
                  href="https://www.tiktok.com/@gistgift2?_r=1&_t=ZT-92krYKklSFJ" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  Follow on TikTok
                </a>
              </Button>
              <Button variant="outline" asChild className="gap-2">
                <a 
                  href="https://x.com/gistgiftapp?s=21" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Follow on X
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Legal & Support Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 mb-8 sm:mb-12">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/privacy-policy')}>
            <CardHeader>
              <Shield className="h-8 w-8 mb-2 text-primary" />
              <CardTitle>Privacy Policy</CardTitle>
              <CardDescription>
                Learn how we protect your data and privacy
              </CardDescription>
            </CardHeader>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/terms-of-service')}>
            <CardHeader>
              <FileText className="h-8 w-8 mb-2 text-primary" />
              <CardTitle>Terms of Use (EULA)</CardTitle>
              <CardDescription>
                Read our terms and conditions of service
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Mail className="h-8 w-8 mb-2 text-primary" />
              <CardTitle>Support</CardTitle>
              <CardDescription>
                Need help? Contact us at:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <a href="mailto:j01652285@gmail.com" className="text-primary hover:underline font-medium">
                GistGift Support
              </a>
              <p className="text-sm text-muted-foreground mt-4">
                We typically respond within 24-48 hours
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground border-t pt-6 sm:pt-8">
          <p className="mb-2">© 2025 GistGift. All rights reserved.</p>
          <div className="flex gap-x-3 gap-y-2 justify-center flex-wrap">
            <Link to="/privacy-policy" className="hover:text-primary">
              Privacy Policy
            </Link>
            <span>•</span>
            <Link to="/terms-of-service" className="hover:text-primary">
              Terms of Use
            </Link>
            <span>•</span>
            <Link to="/about" className="hover:text-primary">
              About
            </Link>
            <span>•</span>
            <Link to="/partners" className="hover:text-primary">
              Partner With Us
            </Link>
            <span>•</span>
            <a href="mailto:support@gistgift.com" className="hover:text-primary">
              Support
            </a>
          </div>
          
          {/* SEO Internal Links */}
          <Collapsible open={exploreOpen} onOpenChange={setExploreOpen} className="mt-6 pt-4 border-t border-border/40">
            <CollapsibleTrigger className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mx-auto min-h-[36px]">
              Explore More
              <ChevronDown className={`h-3 w-3 transition-transform ${exploreOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="flex gap-2 justify-center flex-wrap text-xs mt-3">
                {[
                  { to: "/ai-gift-finder", label: "AI Gift Finder" },
                  { to: "/gift-ideas-by-personality", label: "Gift Ideas by Personality" },
                  { to: "/last-minute-gift-ideas", label: "Last-Minute Gifts" },
                  { to: "/birthday-gift-ideas", label: "Birthday Gifts" },
                  { to: "/anniversary-gift-ideas", label: "Anniversary Gifts" },
                  { to: "/valentines-day-gifts", label: "Valentine's Day" },
                  { to: "/christmas-gift-ideas", label: "Christmas Gifts" },
                  { to: "/gifts-for-her", label: "Gifts for Her" },
                  { to: "/gifts-for-him", label: "Gifts for Him" },
                  { to: "/ai-gift-app", label: "AI Gift App" },
                  { to: "/gift-from-photo", label: "Gift From Photo" },
                  { to: "/photo-gift-finder", label: "Photo Gift Finder" },
                  { to: "/gift-generator-iphone", label: "Gift Generator iPhone" },
                  { to: "/personalized-gift-app", label: "Personalized Gift App" },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="px-3 py-1.5 rounded-full bg-muted/60 hover:bg-primary/10 hover:text-primary transition-colors min-h-[32px] inline-flex items-center"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
          
            <p className="mt-4 text-xs">Version 2.1.7</p>
          </div>
        </div>
      </main>
    </>
  );
};

export default Index;